import type { RiskAssessmentResponse, RiskAssessmentSection } from '@gcv/shared';

import { isScoredQuestion } from 'util/risk-assessment.util';

export interface ScoredQuestionProgress {
  answered: number;
  total: number;
  unansweredFieldIds: string[];
}

/** Mirrors the API's notion of unanswered, so the UI and the completion guard agree. */
const isUnanswered = (response: RiskAssessmentResponse | undefined): boolean =>
  response === undefined ||
  response === null ||
  (typeof response === 'string' && response.trim().length === 0) ||
  (Array.isArray(response) && response.length === 0);

/**
 * Progress over the questions that actually score. Unscored questions are excluded because the
 * denominator has to match what the API requires before it will accept a completion.
 */
export const getScoredQuestionProgress = (
  sections: RiskAssessmentSection[] | undefined,
  responses: { [field_id: string]: RiskAssessmentResponse } | undefined
): ScoredQuestionProgress => {
  const answers = responses ?? {};
  const scored = (sections ?? []).flatMap((section) => (section.fields ?? []).filter(isScoredQuestion));
  const unansweredFieldIds = scored.filter((field) => isUnanswered(answers[field.id])).map((f) => f.id);

  return {
    answered: scored.length - unansweredFieldIds.length,
    total: scored.length,
    unansweredFieldIds
  };
};
